import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sun,
  Moon,
  DollarSign,
  TrendingUp,
  BarChart2,
  Activity,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  LineChart,
  Line,
} from "recharts";
import SideBarNavDash from "../../componets/SideBarNavDash";
import { getDre, getDadosDrePlanilha, getDadosDreOrcamentoPlanilha, getDadosFluxoCaixa } from "../../api";
import { IndicadoresFinanceiros } from "../../components/dashboard/IndicadoresFinanceiros";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v) || 0);

const fmtCompact = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `R$ ${(n / 1_000).toFixed(1)}K`;
  return fmtBRL(n);
};

const fmtPct = (v) => {
  if (v === null || v === undefined) return "—";
  return `${(Number(v) * 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
};

const normalizar = (str) =>
  (str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim();

const isPercentual = (descricao) => (descricao || "").includes("%");

// Retorna valor monetário (ignora linhas que são %) para um conjunto de linhas
const buscarValor = (lista, termos) => {
  const item = lista.find((d) => {
    if (isPercentual(d.descricao)) return false;
    const n = normalizar(d.descricao);
    return termos.some((t) => n.includes(t));
  });
  return item ? Number(item.valor) : null;
};

// Busca por correspondência exata (necessário para distinguir EBIT de EBITDA)
const buscarValorExato = (lista, exato) => {
  const item = lista.find(
    (d) => !isPercentual(d.descricao) && normalizar(d.descricao) === exato
  );
  return item ? Number(item.valor) : null;
};

// Soma valores de mesma descrição (agregar múltiplos meses)
const somarPorDescricao = (lista) => {
  const mapa = {};
  lista.forEach(({ descricao, valor }) => {
    mapa[descricao] = (mapa[descricao] || 0) + Number(valor);
  });
  return Object.entries(mapa).map(([descricao, valor]) => ({ descricao, valor }));
};

// ─── Constantes ───────────────────────────────────────────────────────────────

const MESES = [
  { value: 1,  label: "Jan" },
  { value: 2,  label: "Fev" },
  { value: 3,  label: "Mar" },
  { value: 4,  label: "Abr" },
  { value: 5,  label: "Mai" },
  { value: 6,  label: "Jun" },
  { value: 7,  label: "Jul" },
  { value: 8,  label: "Ago" },
  { value: 9,  label: "Set" },
  { value: 10, label: "Out" },
  { value: 11, label: "Nov" },
  { value: 12, label: "Dez" },
];

const anoAtual = new Date().getFullYear();
const ANO_PLANILHA = 2026;
const ANOS = Array.from({ length: anoAtual - 2022 + 2 }, (_, i) => 2022 + i);

const MESES_PT = {
  janeiro: 1, fevereiro: 2, março: 3, "marco": 3, abril: 4,
  maio: 5, junho: 6, julho: 7, agosto: 8,
  setembro: 9, outubro: 10, novembro: 11, dezembro: 12,
};

const parseBRL = (v) => {
  if (v == null || v === "") return 0;
  if (typeof v === "number") return v;
  const s = String(v).trim().replace(/\./g, "").replace(",", ".");
  return parseFloat(s) || 0;
};

const KPIS = [
  { key: "fat_bruto",        label: "Fat. Bruto",     termos: ["faturamento bruto", "receita bruta"],           icon: DollarSign, color: "#7C3AED" },
  { key: "fat_liquido",      label: "Fat. Líquido",   termos: ["faturamento liquido", "receita liquida"],        icon: TrendingUp, color: "#2563EB" },
  { key: "lucro_bruto",      label: "Lucro Bruto",    termos: ["lucro bruto"],                                   icon: BarChart2,  color: "#16A34A" },
  { key: "ebitda",           label: "EBITDA",          termos: ["ebitda"],                                        icon: Activity,   color: "#D97706" },
  { key: "resultado_liquido",label: "Result. Líquido", termos: ["resultado liquido"],                             icon: PieChart,   color: "#DB2777" },
];

// ─── Sub-componentes ──────────────────────────────────────────────────────────

const VarBadge = ({ atual, anterior }) => {
  if (atual === null || anterior === null || anterior === 0)
    return <span style={{ color: "#9CA3AF", fontSize: 10 }}>—</span>;
  const diff = ((atual - anterior) / Math.abs(anterior)) * 100;
  const zero = Math.abs(diff) < 0.05;
  const pos = diff >= 0;
  const cor = zero ? "#6B7280" : pos ? "#16A34A" : "#DC2626";
  const Icon = zero ? Minus : pos ? ArrowUpRight : ArrowDownRight;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 2, fontSize: 10, fontWeight: 700, color: cor }}>
      <Icon size={11} />
      {zero ? "0,0%" : `${pos ? "+" : ""}${diff.toFixed(1)}%`}
    </span>
  );
};

const KpiCard = ({ kpi, valor, valorAnterior, C, isLight }) => {
  const Icon = kpi.icon;
  return (
    <div
      style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: "18px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        boxShadow: isLight ? "0 1px 4px rgba(0,0,0,0.06)" : "0 1px 4px rgba(0,0,0,0.3)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <div style={{
            width: 30, height: 30, borderRadius: 8,
            background: `${kpi.color}18`,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <Icon size={15} color={kpi.color} />
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {kpi.label}
          </span>
        </div>
        <VarBadge atual={valor} anterior={valorAnterior} />
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: valor === null ? C.muted : C.text }}>
        {valor === null ? "—" : fmtCompact(valor)}
      </div>
      {valor !== null && (
        <div style={{ fontSize: 10, color: C.muted }}>{fmtBRL(valor)}</div>
      )}
    </div>
  );
};

const MargemRow = ({ label, valor, cor, C }) => (
  <div style={{
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 16px", borderBottom: `1px solid ${C.border}`,
  }}>
    <span style={{ fontSize: 13, color: C.text }}>{label}</span>
    <span style={{ fontSize: 14, fontWeight: 800, color: valor === null ? C.muted : cor }}>
      {valor === null ? "—" : fmtPct(valor)}
    </span>
  </div>
);

// ─── Estilos ──────────────────────────────────────────────────────────────────

const btnStyle = {
  display: "flex", alignItems: "center", gap: 6,
  background: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.25)",
  borderRadius: 8, padding: "7px 12px",
  cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
  fontFamily: "inherit",
};

// ─── Página principal ─────────────────────────────────────────────────────────

export default function VisaoGeral() {
  const navigate = useNavigate();
  const [isLight, setIsLight] = useState(true);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);

  // allDados: array normalizado com { descricao, valor, mes, ano }
  const [allDados, setAllDados] = useState([]);
  const [allDadosOrcamento, setAllDadosOrcamento] = useState([]);
  const [dadosFluxoCaixa, setDadosFluxoCaixa] = useState([]);

  const [ano, setAno] = useState(anoAtual);
  const [mesesSel, setMesesSel] = useState([]);

  const C = {
    bg:      isLight ? "#f8fafc" : "#0f172a",
    card:    isLight ? "#ffffff" : "#1e293b",
    border:  isLight ? "#e2e8f0" : "#334155",
    text:    isLight ? "#1e293b" : "#f1f5f9",
    muted:   isLight ? "#64748b" : "#94a3b8",
    brand:   "#7C3AED",
    success: "#16A34A",
    danger:  "#DC2626",
    amber:   "#D97706",
    indigo:  "#6366f1",
    cyan:    "#0891B2",
  };

  // ── Busca dados: DB para anos históricos, Planilha para ANO_PLANILHA ──────
  useEffect(() => {
    const buscar = async () => {
      setLoading(true);
      setErro(false);
      try {
        // Anos distintos que podem ser necessários: o ano selecionado + anterior (para variação)
        const anosNecessarios = [...new Set([ano, ano - 1])];
        const anosDB       = anosNecessarios.filter((a) => a !== ANO_PLANILHA);
        const precisaPlanilha = anosNecessarios.includes(ANO_PLANILHA);

        const promessas = anosDB.map(async (a) => {
          const params = new URLSearchParams({ ano: a });
          const res = await getDre(params.toString());
          return (res || []).map((d) => ({ ...d, ano: d.ano ?? a, mes: Number(d.mes) }));
        });

        if (precisaPlanilha) {
          promessas.push(
            getDadosDrePlanilha().then((res) =>
              (res?.data || []).map((d) => ({
                descricao: d["DESCRIÇÃO"],
                valor:     d["VALOR"],
                mes:       Number(d["MÊS"]),
                ano:       Number(d["ANO"]) || ANO_PLANILHA,
              }))
            )
          );
        }

        const resultados = await Promise.all(promessas);
        setAllDados(resultados.flat());

        // Busca DRE Orçamento (sempre da planilha)
        const resOrc = await getDadosDreOrcamentoPlanilha();
        setAllDadosOrcamento(
          (resOrc?.data || []).map((d) => ({
            descricao: d["DESCRIÇÃO"],
            valor:     d["VALOR"],
            mes:       Number(d["MÊS"]),
            ano:       Number(d["ANO"]) || ANO_PLANILHA,
          }))
        );
      } catch (err) {
        console.error("VisaoGeral — erro ao buscar dados:", err);
        setErro(true);
      } finally {
        setLoading(false);
      }
    };

    buscar();
  }, [ano]);

  // ── Busca Fluxo de Caixa uma vez (dados sempre 2026) ─────────────────────
  useEffect(() => {
    getDadosFluxoCaixa()
      .then((res) => setDadosFluxoCaixa(res?.data || []))
      .catch(() => {});
  }, []);

  // ── Meses disponíveis para o ano selecionado ──────────────────────────────
  const mesesDisp = useMemo(() => {
    const set = new Set(
      allDados.filter((d) => Number(d.ano) === ano).map((d) => Number(d.mes)).filter(Boolean)
    );
    return [...set].sort((a, b) => a - b);
  }, [allDados, ano]);

  // Reset de meses ao trocar ano
  const handleAnoChange = (novoAno) => {
    setAno(novoAno);
    setMesesSel([]);
  };

  // ── Agregar dados por período selecionado ─────────────────────────────────
  const filtrarEAgregar = (anoAlvo, meses) => {
    let linhas = allDados.filter((d) => Number(d.ano) === anoAlvo);
    if (meses.length > 0) {
      linhas = linhas.filter((d) => meses.includes(Number(d.mes)));
    }
    return somarPorDescricao(linhas);
  };

  // Período atual
  const mesesAtivos = mesesSel.length > 0 ? mesesSel : mesesDisp;
  const dadosPeriodo = useMemo(
    () => filtrarEAgregar(ano, mesesAtivos),
    [allDados, ano, mesesAtivos],
  );

  // Período anterior: mesmo(s) mês(es) do ano anterior
  const dadosPeriodoAnt = useMemo(
    () => filtrarEAgregar(ano - 1, mesesAtivos),
    [allDados, ano, mesesAtivos],
  );

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const kpiValues    = useMemo(() => KPIS.map((k) => ({ ...k, valor: buscarValor(dadosPeriodo,   k.termos) })), [dadosPeriodo]);
  const kpiValuesAnt = useMemo(() => KPIS.map((k) => ({ ...k, valor: buscarValor(dadosPeriodoAnt, k.termos) })), [dadosPeriodoAnt]);

  const fatLiquido   = buscarValor(dadosPeriodo, ["faturamento liquido", "receita liquida"]);
  const lucroBruto   = buscarValor(dadosPeriodo, ["lucro bruto"]);
  const ebitda       = buscarValor(dadosPeriodo, ["ebitda"]);
  const resultadoLiq = buscarValor(dadosPeriodo, ["resultado liquido"]);

  const margemBruta   = fatLiquido ? lucroBruto   / fatLiquido : null;
  const margemEbitda  = fatLiquido ? ebitda        / fatLiquido : null;
  const margemLiquida = fatLiquido ? resultadoLiq  / fatLiquido : null;

  // ── Orçamento: agregar por período ───────────────────────────────────────
  const dadosOrcamentoPeriodo = useMemo(() => {
    let linhas = allDadosOrcamento.filter((d) => Number(d.ano) === ano);
    if (mesesAtivos.length > 0) {
      linhas = linhas.filter((d) => mesesAtivos.includes(Number(d.mes)));
    }
    return somarPorDescricao(linhas);
  }, [allDadosOrcamento, ano, mesesAtivos]);

  // ── DRE: linhas-chave Orçado x Realizado ─────────────────────────────────
  const dadosDRE = useMemo(() => {
    const pegar = (fonte, termos) => buscarValor(fonte, termos) ?? 0;
    const pegarExato = (fonte, exato) => buscarValorExato(fonte, exato) ?? 0;
    return [
      {
        label: "Fat. Líquido",
        orcado: pegar(dadosOrcamentoPeriodo, ["faturamento liquido", "receita liquida"]),
        realizado: pegar(dadosPeriodo,        ["faturamento liquido", "receita liquida"]),
      },
      {
        label: "Custos Totais",
        orcado: pegar(dadosOrcamentoPeriodo, ["custos totais"]),
        realizado: pegar(dadosPeriodo,        ["custos totais"]),
      },
      {
        label: "Lucro Bruto",
        orcado: pegar(dadosOrcamentoPeriodo, ["lucro bruto"]),
        realizado: pegar(dadosPeriodo,        ["lucro bruto"]),
      },
      {
        label: "SG&A",
        orcado: pegar(dadosOrcamentoPeriodo, ["sg&a"]),
        realizado: pegar(dadosPeriodo,        ["sg&a"]),
      },
      {
        label: "EBITDA",
        orcado: pegar(dadosOrcamentoPeriodo, ["ebitda"]),
        realizado: pegar(dadosPeriodo,        ["ebitda"]),
      },
      {
        label: "EBIT",
        orcado: pegarExato(dadosOrcamentoPeriodo, "ebit"),
        realizado: pegarExato(dadosPeriodo,        "ebit"),
      },
    ];
  }, [dadosPeriodo, dadosOrcamentoPeriodo]);

  // ── Dados do gráfico: um ponto por mês disponível (para IndicadoresFinanceiros) ─
  const dadosGrafico = useMemo(
    () =>
      mesesDisp.map((m) => {
        const linhas = allDados.filter((d) => Number(d.ano) === ano && Number(d.mes) === m);
        const pegar  = (termos) => buscarValor(linhas, termos) ?? 0;
        return {
          label:       MESES.find((x) => x.value === m)?.label ?? String(m),
          fatLiquido:  pegar(["faturamento liquido", "receita liquida"]),
          lucroBruto:  pegar(["lucro bruto"]),
          ebitda:      pegar(["ebitda"]),
          resultadoLiq:pegar(["resultado liquido"]),
        };
      }),
    [allDados, ano, mesesDisp],
  );

  // ── 6 barras horizontais: Acumulado Orçado x Realizado ───────────────────
  const dadosAcumulado = useMemo(() => {
    const pegar = (fonte, termos) => buscarValor(fonte, termos) ?? 0;
    const recReal = pegar(dadosPeriodo,          ["faturamento bruto", "receita bruta"]);
    const resReal = pegar(dadosPeriodo,          ["resultado liquido"]);
    const recOrc  = pegar(dadosOrcamentoPeriodo, ["faturamento bruto", "receita bruta"]);
    const resOrc  = pegar(dadosOrcamentoPeriodo, ["resultado liquido"]);
    return [
      { label: "Rec. Orçado",     valor: recOrc            },
      { label: "Rec. Realizado",  valor: recReal           },
      { label: "Desp. Orçado",    valor: recOrc  - resOrc  },
      { label: "Desp. Realizado", valor: recReal - resReal },
      { label: "Res. Orçado",     valor: resOrc            },
      { label: "Res. Realizado",  valor: resReal           },
    ];
  }, [dadosPeriodo, dadosOrcamentoPeriodo]);

  // ── Fluxo de Caixa — 5ª Semana de cada mês em 2026 ──────────────────────
  const dadosFluxoProcessado = useMemo(() => {
    const mesAtual = new Date().getMonth() + 1; // junho = 6; realizado = mês < mesAtual
    return dadosFluxoCaixa
      .filter((d) => {
        const anoD = String(d["Ano"] ?? d["ANO"] ?? "").trim();
        const sem  = String(d["Semana"]          ?? "").trim();
        return anoD === "2026" && sem.includes("5");
      })
      .map((d) => {
        const mesStr = String(d["Mês"] ?? d["MES"] ?? "").trim().toLowerCase()
          .normalize("NFD").replace(/[̀-ͯ]/g, "");
        const num   = MESES_PT[mesStr] ?? 0;
        const label = MESES.find((m) => m.value === num)?.label ?? mesStr;
        const valor = parseBRL(d["(=) Resultado"]);
        const tipo  = num < mesAtual ? "Realizado" : "Projetado";
        return { label, num, valor, tipo };
      })
      .filter((d) => d.num > 0)
      .sort((a, b) => a.num - b.num);
  }, [dadosFluxoCaixa]);

  // ── Fluxo filtrado pelo período selecionado na página ─────────────────────
  const dadosFluxoFiltrado = useMemo(() => {
    return dadosFluxoCaixa.filter((d) => {
      const anoD = String(d["Ano"] ?? d["ANO"] ?? "").trim();
      if (anoD !== String(ano)) return false;
      if (mesesSel.length === 0) return true;
      const mesStr = String(d["Mês"] ?? "").trim().toLowerCase()
        .normalize("NFD").replace(/[̀-ͯ]/g, "");
      return mesesSel.includes(MESES_PT[mesStr] ?? 0);
    });
  }, [dadosFluxoCaixa, ano, mesesSel]);

  const toggleMes = (m) =>
    setMesesSel((prev) => prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]);

  const selectEl = {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 7,
    color: C.text,
    fontSize: 12,
    padding: "6px 10px",
    cursor: "pointer",
    fontFamily: "inherit",
  };

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavDash isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>

        {/* ── Header ── */}
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)",
          color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)", flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ marginLeft: 12, fontSize: 13, opacity: 0.7 }}>/ Visão Geral</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={() => setIsLight((v) => !v)} style={btnStyle}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />}
              {isLight ? "Claro" : "Escuro"}
            </button>
            <button onClick={() => navigate("/inicio")} style={btnStyle}>
              Menu Principal
            </button>
          </div>
        </header>

        <main style={{ padding: "24px 28px", maxWidth: 1400, margin: "0 auto", width: "100%" }}>

          {/* ── Filtros ── */}
          <div style={{
            background: C.card, border: `1px solid ${C.border}`, borderRadius: 10,
            padding: "14px 18px", marginBottom: 20,
            display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
            boxShadow: isLight ? "0 1px 4px rgba(0,0,0,0.06)" : "0 1px 4px rgba(0,0,0,0.3)",
          }}>
            {/* Seletor de Ano */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase" }}>Ano</span>
              <select value={ano} onChange={(e) => handleAnoChange(Number(e.target.value))} style={selectEl}>
                {ANOS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>

            {/* Pills de mês */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", marginRight: 2 }}>
                Mês
              </span>
              {MESES.map(({ value, label }) => {
                const disponivel = mesesDisp.includes(value);
                const sel = mesesSel.includes(value);
                return (
                  <button
                    key={value}
                    disabled={!disponivel}
                    onClick={() => toggleMes(value)}
                    style={{
                      fontSize: 11, fontWeight: sel ? 700 : 500,
                      padding: "4px 10px", borderRadius: 20,
                      border: `1px solid ${sel ? C.brand : C.border}`,
                      background: sel ? `${C.brand}18` : "transparent",
                      color: !disponivel ? C.border : sel ? C.brand : C.text,
                      cursor: disponivel ? "pointer" : "default",
                      transition: "all 0.15s",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
              {mesesSel.length > 0 && (
                <button
                  onClick={() => setMesesSel([])}
                  style={{ fontSize: 11, color: C.muted, background: "none", border: "none", cursor: "pointer", padding: "4px 6px" }}
                >
                  Limpar
                </button>
              )}
            </div>

            {/* Indicador de período ativo */}
            {!loading && (
              <span style={{ fontSize: 11, color: C.muted, marginLeft: "auto" }}>
                {mesesAtivos.length === mesesDisp.length
                  ? `Acumulado ${ano}`
                  : `${mesesAtivos.map((m) => MESES.find((x) => x.value === m)?.label).join(", ")} / ${ano}`}
                {` vs mesmo período ${ano - 1}`}
              </span>
            )}
          </div>

          {/* ── Loading / Erro ── */}
          {loading && (
            <div style={{ padding: 80, textAlign: "center", color: C.muted, fontSize: 14 }}>
              Carregando dados…
            </div>
          )}

          {!loading && erro && (
            <div style={{ padding: 80, textAlign: "center", color: C.danger, fontSize: 14 }}>
              Erro ao carregar dados. Verifique a conexão com o backend.
            </div>
          )}

          {!loading && !erro && (
            <>
              {/* ── KPI Cards ── */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 14, marginBottom: 20 }}>
                {kpiValues.map((kpi, i) => (
                  <KpiCard
                    key={kpi.key}
                    kpi={kpi}
                    valor={kpi.valor}
                    valorAnterior={kpiValuesAnt[i]?.valor ?? null}
                    C={C}
                    isLight={isLight}
                  />
                ))}
              </div>

              {/* ── Gráfico mensal + Comparativo + Painel de margens ── */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 280px", gap: 20, marginBottom: 20 }}>

                {/* Gráfico: 6 barras horizontais — Acumulado Orçado x Realizado */}
                <div style={{
                  background: C.card, border: `1px solid ${C.border}`,
                  borderRadius: 12, padding: 20,
                  boxShadow: isLight ? "0 1px 4px rgba(0,0,0,0.06)" : "0 1px 4px rgba(0,0,0,0.3)",
                }}>
                  <span style={{
                    fontWeight: 700, fontSize: 13, color: C.muted,
                    textTransform: "uppercase", letterSpacing: "0.05em",
                    display: "block", marginBottom: 4,
                  }}>
                    Orçado × Realizado — {ano}
                  </span>
                  <span style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 16 }}>
                    Acumulado · Receitas · Despesas · Resultado
                  </span>
                  <div style={{ width: "100%", height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={dadosAcumulado}
                        layout="vertical"
                        margin={{ top: 4, right: 90, bottom: 4, left: 4 }}
                        barCategoryGap="18%"
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
                        <XAxis
                          type="number"
                          stroke={C.muted} fontSize={10} tickLine={false} axisLine={false}
                          tickFormatter={(v) =>
                            Math.abs(v) >= 1_000_000
                              ? `${(v / 1_000_000).toFixed(1)}M`
                              : `${(v / 1_000).toFixed(0)}k`
                          }
                        />
                        <YAxis
                          dataKey="label"
                          type="category"
                          stroke={C.muted} fontSize={11} tickLine={false} axisLine={false}
                          width={110}
                        />
                        <Tooltip
                          contentStyle={{ backgroundColor: C.card, borderColor: C.border, borderRadius: 8, color: C.text, fontSize: 12 }}
                          cursor={{ fill: isLight ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.04)" }}
                          formatter={(value, name) => [fmtBRL(value), name]}
                        />
                        <ReferenceLine x={0} stroke={C.border} />
                        <Bar
                          dataKey="valor"
                          radius={[0, 4, 4, 0]}
                          maxBarSize={30}
                          label={{ position: "right", fontSize: 10, fill: C.muted, formatter: (v) => v ? fmtCompact(v) : "" }}
                        >
                          {dadosAcumulado.map((_, i) => (
                            <Cell
                              key={i}
                              fill={[
                                isLight ? "#94A3B8" : "#64748B",
                                "#16A34A",
                                isLight ? "#94A3B8" : "#64748B",
                                "#16A34A",
                                isLight ? "#94A3B8" : "#64748B",
                                "#16A34A",
                              ][i]}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Gráfico DRE — Orçado × Realizado (horizontal) */}
                <div style={{
                  background: C.card, border: `1px solid ${C.border}`,
                  borderRadius: 12, padding: 20,
                  boxShadow: isLight ? "0 1px 4px rgba(0,0,0,0.06)" : "0 1px 4px rgba(0,0,0,0.3)",
                }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
                    <div>
                      <span style={{
                        fontWeight: 700, fontSize: 13, color: C.muted,
                        textTransform: "uppercase", letterSpacing: "0.05em",
                        display: "block", marginBottom: 2,
                      }}>
                        DRE — Acumulado {ano}
                      </span>
                      <span style={{ fontSize: 11, color: C.muted }}>Orçado × Realizado · Linhas-chave</span>
                    </div>
                    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                      {[
                        { cor: isLight ? "#94A3B8" : "#475569", label: "Orçado" },
                        { cor: "#16A34A", label: "Realizado" },
                      ].map(({ cor, label }) => (
                        <div key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <span style={{ width: 10, height: 10, borderRadius: 3, background: cor, display: "inline-block", flexShrink: 0 }} />
                          <span style={{ fontSize: 11, color: C.muted }}>{label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ width: "100%", height: 320 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={dadosDRE}
                        layout="vertical"
                        margin={{ top: 4, right: 100, bottom: 4, left: 4 }}
                        barCategoryGap="22%"
                        barGap={3}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
                        <XAxis
                          type="number"
                          stroke={C.muted} fontSize={10} tickLine={false} axisLine={false}
                          tickFormatter={(v) =>
                            Math.abs(v) >= 1_000_000
                              ? `${(v / 1_000_000).toFixed(1)}M`
                              : `${(v / 1_000).toFixed(0)}k`
                          }
                        />
                        <YAxis
                          dataKey="label"
                          type="category"
                          stroke={C.muted} fontSize={11} tickLine={false} axisLine={false}
                          width={90}
                        />
                        <Tooltip
                          contentStyle={{ backgroundColor: C.card, borderColor: C.border, borderRadius: 8, color: C.text, fontSize: 12 }}
                          cursor={{ fill: isLight ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.04)" }}
                          formatter={(value, name) => [fmtBRL(value), name]}
                        />
                        <ReferenceLine x={0} stroke={C.border} />
                        <Bar
                          dataKey="orcado"
                          name="Orçado"
                          fill={isLight ? "#94A3B8" : "#475569"}
                          radius={[0, 4, 4, 0]}
                          maxBarSize={18}
                        />
                        <Bar
                          dataKey="realizado"
                          name="Realizado"
                          fill="#16A34A"
                          radius={[0, 4, 4, 0]}
                          maxBarSize={18}
                          label={{ position: "right", fontSize: 10, fill: C.muted, formatter: (v) => v ? fmtCompact(v) : "" }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Painel de Margens */}
                <div style={{
                  background: C.card, border: `1px solid ${C.border}`,
                  borderRadius: 12, overflow: "hidden",
                  boxShadow: isLight ? "0 1px 4px rgba(0,0,0,0.06)" : "0 1px 4px rgba(0,0,0,0.3)",
                }}>
                  <div style={{
                    padding: "14px 16px", borderBottom: `1px solid ${C.border}`,
                    fontWeight: 700, fontSize: 13, color: C.muted,
                    textTransform: "uppercase", letterSpacing: "0.05em",
                  }}>
                    Margens do Período
                  </div>
                  <MargemRow label="Margem Bruta"   valor={margemBruta}   cor={margemBruta  !== null && margemBruta  >= 0 ? C.success : C.danger} C={C} />
                  <MargemRow label="Margem EBITDA"  valor={margemEbitda}  cor={margemEbitda !== null && margemEbitda >= 0 ? C.amber   : C.danger} C={C} />
                  <MargemRow label="Margem Líquida" valor={margemLiquida} cor={margemLiquida!== null && margemLiquida>= 0 ? C.cyan    : C.danger} C={C} />

                  <div style={{ padding: "16px 16px 12px" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", marginBottom: 10, letterSpacing: "0.05em" }}>
                      Resumo Valores
                    </div>
                    {[
                      { label: "Fat. Líquido",  valor: fatLiquido,   cor: "#2563EB" },
                      { label: "Lucro Bruto",   valor: lucroBruto,   cor: C.success },
                      { label: "EBITDA",         valor: ebitda,       cor: C.amber   },
                      { label: "Result. Líq.",   valor: resultadoLiq, cor: "#DB2777" },
                    ].map(({ label, valor, cor }) => (
                      <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 7 }}>
                        <span style={{ fontSize: 12, color: C.muted }}>{label}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: valor !== null ? cor : C.muted }}>
                          {valor !== null ? fmtCompact(valor) : "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Fluxo de Caixa — Resultado 5ª Semana 2026 ── */}
              {dadosFluxoProcessado.length > 0 && (
                <div style={{
                  background: C.card, border: `1px solid ${C.border}`,
                  borderRadius: 12, padding: 20, marginBottom: 20,
                  boxShadow: isLight ? "0 1px 4px rgba(0,0,0,0.06)" : "0 1px 4px rgba(0,0,0,0.3)",
                }}>
                  {/* Cabeçalho + legenda */}
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
                    <div>
                      <span style={{ fontWeight: 700, fontSize: 13, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 3 }}>
                        Fluxo de Caixa — Resultado Mensal · 2026
                      </span>
                      <span style={{ fontSize: 11, color: C.muted }}>
                        Acumulado até a 5ª Semana de cada mês
                      </span>
                    </div>

                    {/* Legenda customizada */}
                    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
                      {[
                        { cor: "#16A34A", label: "Realizado (+)" },
                        { cor: "#DC2626", label: "Realizado (−)" },
                        { cor: "#60A5FA", label: "Projetado (+)" },
                        { cor: "#FB923C", label: "Projetado (−)" },
                      ].map(({ cor, label }) => (
                        <div key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <span style={{ width: 10, height: 10, borderRadius: "50%", background: cor, display: "inline-block", flexShrink: 0 }} />
                          <span style={{ fontSize: 11, color: C.muted }}>{label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ width: "100%", height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={dadosFluxoProcessado}
                        margin={{ top: 30, right: 20, bottom: 0, left: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                        <XAxis
                          dataKey="label"
                          stroke={C.muted} fontSize={12} tickLine={false} axisLine={false} dy={8}
                        />
                        <YAxis
                          stroke={C.muted} fontSize={10} tickLine={false} axisLine={false} dx={-4}
                          tickFormatter={(v) =>
                            Math.abs(v) >= 1_000_000
                              ? `${(v / 1_000_000).toFixed(1)}M`
                              : `${(v / 1_000).toFixed(0)}k`
                          }
                        />
                        <Tooltip
                          contentStyle={{ backgroundColor: C.card, borderColor: C.border, borderRadius: 8, color: C.text, fontSize: 12 }}
                          cursor={{ stroke: C.border, strokeWidth: 1 }}
                          formatter={(v, _, props) => [fmtBRL(v), `(=) Resultado · ${props.payload?.tipo ?? ""}`]}
                        />
                        <ReferenceLine y={0} stroke={C.border} />
                        <Line
                          dataKey="valor"
                          stroke={C.brand}
                          strokeWidth={2}
                          dot={(props) => {
                            const { cx, cy, payload } = props;
                            const cor = payload.tipo === "Realizado"
                              ? (payload.valor >= 0 ? "#16A34A" : "#DC2626")
                              : (payload.valor >= 0 ? "#60A5FA" : "#FB923C");
                            return (
                              <circle
                                key={`dot-${payload.label}`}
                                cx={cx} cy={cy} r={5}
                                fill={cor}
                                stroke={isLight ? "#fff" : C.card}
                                strokeWidth={2}
                              />
                            );
                          }}
                          activeDot={{ r: 7, stroke: C.brand, strokeWidth: 2 }}
                          label={{
                            position: "top", fontSize: 10, fill: C.muted,
                            formatter: (v) => v ? fmtCompact(v) : "",
                          }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* ── Indicadores Financeiros ── */}
              {dadosGrafico.length > 0 && (
                <IndicadoresFinanceiros dadosGrafico={dadosGrafico} dadosFluxo={dadosFluxoFiltrado} />
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
